'use client';
import { CookieStorageContextProvider, type InitialCookieValues } from '@/contexts/CookieContext';
import { ModalProvider, ModalHost } from '@/contexts/ModalContext';
import { QueryProvider } from '@/contexts/QueryContext';
import { TelegramMiniAppProvider } from '@/contexts/TelegramMiniAppContext';
import { NextIntlClientProvider } from 'next-intl';
import { ReactNode } from 'react';
import { Direction } from 'radix-ui';

import { ProgressProvider } from '@bprogress/next/app';
import { LocalStorageContextProvider } from '@/contexts/LocalStorageContext';
import { LoginContextProvider } from '@/contexts/LoginContext';
import { MonetizationContextProvider } from '@/contexts/MonetizationContext';
import { UserDeletionContextProvider } from '@/contexts/UserDeletionContext';
import { BonusCoinsContextProvider } from '@/contexts/BonusCoinsContext';
import { Toaster } from '@/components/ui/sonner';
import { NetworkStatusToasts } from '@/components/NetworkStatusToasts';
import { AnchorProgress } from '@/components/AnchorProgress';

type Props = {
  children: ReactNode;
  locale: string;
  messages: any;
  timeZone: string;
  telegramBotUsername?: string;
  initialCookies?: InitialCookieValues;
};

export function Providers({
  children,
  locale,
  messages,
  timeZone,
  telegramBotUsername = '@testdoskalocalhostbot',
  initialCookies,
}: Props) {
  return (
    <Direction.DirectionProvider dir={locale === 'he' ? 'rtl' : 'ltr'}>
      <NextIntlClientProvider locale={locale} messages={messages} timeZone={timeZone}>
        <ProgressProvider
          height='5px'
          color='var(--color-main-green)'
          options={{ showSpinner: false }}
          shallowRouting
          disableAnchorClick
        >
          <AnchorProgress />
          <QueryProvider>
            <ModalProvider>
              <TelegramMiniAppProvider telegramBotUsername={telegramBotUsername}>
                <CookieStorageContextProvider initialValues={initialCookies}>
                  <LocalStorageContextProvider>
                    <LoginContextProvider>
                      <ModalHost />
                      <Toaster />
                      <NetworkStatusToasts />
                      <UserDeletionContextProvider>
                        <BonusCoinsContextProvider>
                          <MonetizationContextProvider>{children}</MonetizationContextProvider>
                        </BonusCoinsContextProvider>
                      </UserDeletionContextProvider>
                    </LoginContextProvider>
                  </LocalStorageContextProvider>
                </CookieStorageContextProvider>
              </TelegramMiniAppProvider>
            </ModalProvider>
          </QueryProvider>
        </ProgressProvider>
      </NextIntlClientProvider>
    </Direction.DirectionProvider>
  );
}
